/**
 * Release 2 S3 (A) — a smart board: its definition evaluated live (narrowed by the API to the boards the viewer can
 * open) AND the caller's filters. Read-only grid; "Edit definition" for members with MANAGE_BOARD on it.
 */
import { useState } from 'react';
import { Sparkles } from 'lucide-react';
import type { Board } from '@/lib/api/types';
import { useBoardAssetsR1 } from '@/lib/api/queries';
import { Button } from '@/components/Button';
import { BoardTidyActions } from '@/features/pins/BoardTidyActions';
import { usePermission } from '@/features/workspace/usePermission';
import { countBoardFilters } from '@/features/search/predicates/serialize';
import { SmartBoardDialog } from './SmartBoardDialog';
import { VirtualBoardView, useVirtualBoardUrl } from './VirtualBoardView';
import { DEFAULT_SMART_SORT, type SmartSort } from './definition';
import { useSmartBoardLive } from './useSmartBoardRooms';

export function SmartBoardPage({ board }: { board: Board }) {
  useSmartBoardLive(board); // own room (board.updated = new definition) + watched boards' asset events, lib/ws/apply.ts section A
  const url = useVirtualBoardUrl((board.smart_sort as SmartSort | null | undefined) ?? DEFAULT_SMART_SORT);
  const [toggled, setToggled] = useState<boolean | null>(null);
  const filtersOpen = toggled ?? countBoardFilters(url.filters) > 0;
  const canEdit = usePermission('MANAGE_BOARD', { boardId: board.id });
  const [editing, setEditing] = useState(false);
  const listing = useBoardAssetsR1(board.id, { sort: url.sort, includeDescendants: false, filter: 'all', filters: url.filtersParam, facets: filtersOpen });
  return (
    <>
      <VirtualBoardView
        title={board.name}
        badge={
          <>
            <span className="inline-flex items-center gap-1 rounded-full bg-accent-soft px-2 py-0.5 text-xs text-accent" data-smart-badge><Sparkles size={11} /> Smart</span>
            <BoardTidyActions board={board} />
          </>
        }
        description={board.description}
        actions={canEdit && <Button size="sm" onClick={() => setEditing(true)} data-edit-definition>Edit definition</Button>}
        listing={listing}
        filters={url.filters}
        onFilters={url.setFilters}
        filtersOpen={filtersOpen}
        onToggleFilters={setToggled}
        sort={url.sort}
        onSort={url.setSort}
        emptyTitle="Nothing matches this smart board yet"
        emptyBody="Assets show up here as soon as they match its definition."
        label={`${board.name} assets`}
      />
      {editing && <SmartBoardDialog mode="edit" board={board} onClose={() => setEditing(false)} />}
    </>
  );
}
